/**
 * HyperCoderView — Dev ▸ HyperCoder (kill the Potemkin route).
 *
 * The Dev dropdown always advertised "HyperCoder — pair-coding agent that
 * ships PRs end-to-end", but the entry dead-ended on the generic Tools hub:
 * no purpose, no state, no dispatch. This view is the real destination —
 * the governed coding-agent dispatch console:
 *
 *   • PURPOSE header: what HyperCoder actually is — a ticket becomes a
 *     scoped DispatchContract, a worker returns a reviewable file patch,
 *     scope gates enforce the blast radius BEFORE any diff exists, and
 *     every approved patch ships with its exact inverse.
 *   • DISPATCH: pick an open ticket and run the real DispatchPanel
 *     (live argoWorker when configured, honest deterministic fallback
 *     when not — the panel labels which).
 *   • PIPELINE: live DevDispatch records from db/devDispatch.ts rendered
 *     across proposed → dispatched → pr_open → merged, each with an
 *     honest status line and a real PR link when one exists.
 *   • HONEST SEAMS: worker Connected/Simulation from argoWorker, and a
 *     GitHub readout that never fakes it — dispatch proposes and reviews
 *     patches in-app; PRs open on GitHub once the operator wires a repo
 *     token into the backend (src/lib/integrations/github.ts is ready).
 *
 * All roll-up math lives in the pure, tested module
 * src/lib/dev/dispatchOverview.ts; this component only renders it.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Bot,
  Code2,
  GitPullRequest,
  Plug,
  ShieldCheck,
  Ticket as TicketIcon,
  Undo2,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { DispatchPanel } from '../components/dev/DispatchPanel';
import { C } from '../lib/tokens';
import { subscribeDevDispatch } from '../lib/db/devDispatch';
import { bootstrapTicketsIfEmpty, subscribeTickets } from '../lib/db/tickets';
import type { DevDispatch, DevDispatchState, Ticket } from '../lib/db/types';
import { workerInfo } from '../lib/agent/argoWorker';
import {
  DISPATCH_STAGES,
  FAILED_STAGE,
  dispatchStatusLine,
  githubLoopStatus,
  summarizeDispatches,
} from '../lib/dev/dispatchOverview';

/** Pipeline-state pills, mapped onto the design system's variants. */
const STATE_PILL: Record<DevDispatchState, string> = {
  proposed: 'glass-pill',
  dispatched: 'glass-pill glass-pill--blue',
  pr_open: 'glass-pill glass-pill--orange',
  merged: 'glass-pill glass-pill--green',
  failed: 'glass-pill glass-pill--magenta',
};

const STATE_LABEL: Record<DevDispatchState, string> = {
  proposed: 'Proposed',
  dispatched: 'Dispatched',
  pr_open: 'PR open',
  merged: 'Merged',
  failed: 'Failed',
};

export function HyperCoderView() {
  const [dispatches, setDispatches] = useState<DevDispatch[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');

  useEffect(() => {
    let active = true;
    void bootstrapTicketsIfEmpty();
    const unsubs = [
      subscribeDevDispatch((r) => active && setDispatches(r)),
      subscribeTickets((r) => active && setTickets(r)),
    ];
    return () => {
      active = false;
      unsubs.forEach((u) => u());
    };
  }, []);

  const summary = useMemo(() => summarizeDispatches(dispatches), [dispatches]);
  const github = useMemo(() => githubLoopStatus(dispatches), [dispatches]);
  const worker = workerInfo();

  // Dispatchable work: tickets that are neither shipped nor cancelled.
  const openTickets = useMemo(
    () => tickets.filter((t) => t.status !== 'done' && t.status !== 'cancelled'),
    [tickets],
  );
  const selectedTicket =
    openTickets.find((t) => t.id === selectedTicketId) ?? null;

  return (
    <div className="app-container" style={{ display: 'grid', gap: 24 }}>
      {/* Purpose header — what this surface actually is. */}
      <header>
        <span className="t-eyebrow">Dev</span>
        <h1 className="t-page-title" style={{ margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Code2 size={26} strokeWidth={1.75} style={{ color: C.magentaSoft }} aria-hidden="true" />
          HyperCoder
        </h1>
        <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
          Governed coding-agent dispatch. A ticket becomes a scoped contract, the agent
          returns a reviewable file patch, scope gates enforce the blast radius before any
          diff exists, and every approved change carries its exact inverse — reviewable in,
          reversible out.
        </p>
      </header>

      {/* Honest connection seams — never simulated. */}
      <section aria-label="Connections">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
          <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bot size={16} strokeWidth={1.75} style={{ color: C.magentaSoft }} aria-hidden="true" />
              <span className="t-section-title">Coding worker</span>
              <span
                className={worker.configured ? 'glass-pill glass-pill--green' : 'glass-pill'}
                style={{ marginLeft: 'auto' }}
              >
                {worker.configured ? 'Connected' : 'Simulation'}
              </span>
            </div>
            <p className="t-meta" style={{ margin: 0, color: C.textMuted }}>
              {worker.configured
                ? `Dispatches run on the live Argo agent worker (${worker.baseUrl}).`
                : 'No agent worker configured — dispatches use the deterministic in-app fallback so the full govern → review → approve → revert loop still works offline.'}
            </p>
          </GlassPanel>
          <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Plug size={16} strokeWidth={1.75} style={{ color: '#7ec1ff' }} aria-hidden="true" />
              <span className="t-section-title">GitHub loop</span>
              <span
                className={github.hasLivePrs ? 'glass-pill glass-pill--green' : 'glass-pill'}
                style={{ marginLeft: 'auto' }}
              >
                {github.hasLivePrs ? `${github.livePrCount} live PR${github.livePrCount === 1 ? '' : 's'}` : 'Not connected'}
              </span>
            </div>
            <p className="t-meta" style={{ margin: 0, color: C.textMuted }}>
              {github.message}
            </p>
          </GlassPanel>
        </div>
      </section>

      {/* Pipeline strip — how a dispatch flows, with live counts. */}
      <section aria-label="Dispatch pipeline">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={16} strokeWidth={1.75} style={{ color: C.green }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Pipeline</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              How a dispatch flows · {summary.active} active · {summary.merged} merged
              {summary.failed > 0 ? ` · ${summary.failed} failed` : ''}
            </h2>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {DISPATCH_STAGES.map((stage, i) => (
            <GlassPanel key={stage.state} variant="default" style={{ padding: 16, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="t-meta" style={{ color: C.textDim }}>{i + 1}</span>
                <span className={STATE_PILL[stage.state]}>{stage.label}</span>
                <span className="t-meta" style={{ marginLeft: 'auto', color: C.textMuted }}>
                  {summary.counts[stage.state]}
                </span>
              </div>
              <p className="t-meta" style={{ margin: 0, color: C.textMuted }}>{stage.description}</p>
            </GlassPanel>
          ))}
        </div>
        <p className="t-meta" style={{ margin: '10px 0 0', color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Undo2 size={12} aria-hidden="true" />
          {FAILED_STAGE.label}: {FAILED_STAGE.description} ({summary.counts.failed} on record.)
        </p>
      </section>

      {/* Dispatch console — the real thing, not a stub. */}
      <section aria-label="Dispatch a ticket">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <TicketIcon size={16} strokeWidth={1.75} style={{ color: '#ffd06b' }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Dispatch</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>Hand a ticket to the agent</h2>
          </div>
        </div>
        <GlassPanel variant="default" style={{ padding: 20, display: 'grid', gap: 14 }}>
          {openTickets.length === 0 ? (
            <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
              No open tickets to dispatch — create one in Dev ▸ Tickets first.
            </p>
          ) : (
            <label className="t-meta" style={{ display: 'grid', gap: 6, maxWidth: 520 }}>
              Ticket to dispatch
              <select
                value={selectedTicketId}
                onChange={(e) => setSelectedTicketId(e.target.value)}
                style={{
                  background: C.ink800,
                  border: '1px solid rgba(255,255,255,0.09)',
                  borderRadius: 10,
                  color: C.text,
                  padding: '8px 10px',
                  font: 'inherit',
                }}
              >
                <option value="">Select an open ticket…</option>
                {openTickets.map((t) => (
                  <option key={t.id} value={t.id}>
                    [{t.priority.toUpperCase()}] {t.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          {selectedTicket && (
            <DispatchPanel
              key={selectedTicket.id}
              ticket={{ id: selectedTicket.id, title: selectedTicket.title }}
            />
          )}
        </GlassPanel>
      </section>

      {/* Live dispatch record — the persisted DevDispatch feed. */}
      <section aria-label="Dispatch record">
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <GitPullRequest size={16} strokeWidth={1.75} style={{ color: C.magentaSoft }} aria-hidden="true" />
          <div>
            <span className="t-eyebrow">Record</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Dispatches · {summary.total} on record
            </h2>
          </div>
        </div>
        <GlassPanel variant="default" style={{ padding: 20 }}>
          {summary.recent.length === 0 ? (
            <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
              No dispatches yet. Pick an open ticket above, seal its blast radius, and
              dispatch — the approved patch lands here with its full pipeline state.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 12 }}>
              {summary.recent.map((d) => (
                <li key={d.id} style={{ display: 'grid', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span className={STATE_PILL[d.state]}>{STATE_LABEL[d.state]}</span>
                    <span className="t-body" style={{ flex: 1, minWidth: 200 }}>
                      {d.ticketTitle}
                      <span style={{ color: C.textDim }}> · {d.ticketId}{d.repo ? ` · ${d.repo}` : ''}</span>
                    </span>
                    {d.prUrl ? (
                      <a
                        href={d.prUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="glass-pill"
                        style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                      >
                        <GitPullRequest size={12} aria-hidden="true" />
                        {typeof d.prNumber === 'number' ? `PR #${d.prNumber}` : 'Open PR'}
                      </a>
                    ) : null}
                    <span className="t-meta" style={{ color: C.textDim, whiteSpace: 'nowrap' }}>
                      {new Date(d.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <span className="t-meta" style={{ color: C.textMuted }}>
                    {dispatchStatusLine(d)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </GlassPanel>
      </section>
    </div>
  );
}

export default HyperCoderView;
